import { useState, useCallback, useEffect } from 'react';

interface UseSidebarToggleOptions {
    defaultCollapsed?: boolean;
    storageKey?: string;
}

interface UseSidebarToggleReturn {
    isCollapsed: boolean;
    toggle: () => void;
    expand: () => void;
    collapse: () => void;
}

const useSidebarToggle = (options: UseSidebarToggleOptions = {}): UseSidebarToggleReturn => {
    const { defaultCollapsed = false, storageKey = 'sidebarCollapsed' } = options;

    // Initialize state from localStorage or default
    const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(storageKey);
            return saved !== null ? JSON.parse(saved) : defaultCollapsed;
        }
        return defaultCollapsed;
    });

    // Persist state to localStorage whenever it changes
    useEffect(() => {
        if (typeof window !== 'undefined') {
            localStorage.setItem(storageKey, JSON.stringify(isCollapsed));
        }
    }, [isCollapsed, storageKey]);

    const toggle = useCallback(() => {
        setIsCollapsed(prev => !prev);
    }, []);

    const expand = useCallback(() => {
        setIsCollapsed(false);
    }, []);

    const collapse = useCallback(() => {
        setIsCollapsed(true);
    }, []);

    return {
        isCollapsed,
        toggle,
        expand,
        collapse,
    };
};

export default useSidebarToggle; 